/**
 * ISO 3166-1 alpha-2 country code → approximate centroid + display name.
 *
 * Used by the "Requests by Location" geoheat panel to convert analytics rows
 * (`{ country_code: 'US', requests: 1234 }`) into weighted points that the
 * Google Maps `HeatmapLayer` can render.
 *
 * Centroids are roughly the geographic center of each territory — fine for
 * a heatmap visualization (not for navigation).
 */

export interface CountryCentroid {
  name: string;
  lat: number;
  lng: number;
}

export const COUNTRY_CENTROIDS: Readonly<Record<string, CountryCentroid>> = {
  AD: { name: 'Andorra', lat: 42.5, lng: 1.5 },
  AE: { name: 'United Arab Emirates', lat: 24, lng: 54 },
  AF: { name: 'Afghanistan', lat: 33, lng: 65 },
  AG: { name: 'Antigua and Barbuda', lat: 17.05, lng: -61.8 },
  AI: { name: 'Anguilla', lat: 18.25, lng: -63.1667 },
  AL: { name: 'Albania', lat: 41, lng: 20 },
  AM: { name: 'Armenia', lat: 40, lng: 45 },
  AO: { name: 'Angola', lat: -12.5, lng: 18.5 },
  AQ: { name: 'Antarctica', lat: -90, lng: 0 },
  AR: { name: 'Argentina', lat: -34, lng: -64 },
  AS: { name: 'American Samoa', lat: -14.3333, lng: -170 },
  AT: { name: 'Austria', lat: 47.3333, lng: 13.3333 },
  AU: { name: 'Australia', lat: -27, lng: 133 },
  AW: { name: 'Aruba', lat: 12.5, lng: -69.9667 },
  AX: { name: 'Åland Islands', lat: 60.1167, lng: 19.9 },
  AZ: { name: 'Azerbaijan', lat: 40.5, lng: 47.5 },
  BA: { name: 'Bosnia and Herzegovina', lat: 44, lng: 18 },
  BB: { name: 'Barbados', lat: 13.1667, lng: -59.5333 },
  BD: { name: 'Bangladesh', lat: 24, lng: 90 },
  BE: { name: 'Belgium', lat: 50.8333, lng: 4 },
  BF: { name: 'Burkina Faso', lat: 13, lng: -2 },
  BG: { name: 'Bulgaria', lat: 43, lng: 25 },
  BH: { name: 'Bahrain', lat: 26, lng: 50.55 },
  BI: { name: 'Burundi', lat: -3.5, lng: 30 },
  BJ: { name: 'Benin', lat: 9.5, lng: 2.25 },
  BL: { name: 'Saint Barthélemy', lat: 17.9, lng: -62.8333 },
  BM: { name: 'Bermuda', lat: 32.3333, lng: -64.75 },
  BN: { name: 'Brunei', lat: 4.5, lng: 114.6667 },
  BO: { name: 'Bolivia', lat: -17, lng: -65 },
  BQ: { name: 'Caribbean Netherlands', lat: 12.1833, lng: -68.25 },
  BR: { name: 'Brazil', lat: -10, lng: -55 },
  BS: { name: 'Bahamas', lat: 24.25, lng: -76 },
  BT: { name: 'Bhutan', lat: 27.5, lng: 90.5 },
  BV: { name: 'Bouvet Island', lat: -54.4333, lng: 3.4 },
  BW: { name: 'Botswana', lat: -22, lng: 24 },
  BY: { name: 'Belarus', lat: 53, lng: 28 },
  BZ: { name: 'Belize', lat: 17.25, lng: -88.75 },
  CA: { name: 'Canada', lat: 60, lng: -95 },
  CC: { name: 'Cocos (Keeling) Islands', lat: -12.5, lng: 96.8333 },
  CD: { name: 'DR Congo', lat: 0, lng: 25 },
  CF: { name: 'Central African Republic', lat: 7, lng: 21 },
  CG: { name: 'Congo', lat: -1, lng: 15 },
  CH: { name: 'Switzerland', lat: 47, lng: 8 },
  CI: { name: "Côte d'Ivoire", lat: 8, lng: -5 },
  CK: { name: 'Cook Islands', lat: -21.2333, lng: -159.7667 },
  CL: { name: 'Chile', lat: -30, lng: -71 },
  CM: { name: 'Cameroon', lat: 6, lng: 12 },
  CN: { name: 'China', lat: 35, lng: 105 },
  CO: { name: 'Colombia', lat: 4, lng: -72 },
  CR: { name: 'Costa Rica', lat: 10, lng: -84 },
  CU: { name: 'Cuba', lat: 21.5, lng: -80 },
  CV: { name: 'Cabo Verde', lat: 16, lng: -24 },
  CW: { name: 'Curaçao', lat: 12.1667, lng: -68.9667 },
  CX: { name: 'Christmas Island', lat: -10.5, lng: 105.6667 },
  CY: { name: 'Cyprus', lat: 35, lng: 33 },
  CZ: { name: 'Czechia', lat: 49.75, lng: 15.5 },
  DE: { name: 'Germany', lat: 51, lng: 9 },
  DJ: { name: 'Djibouti', lat: 11.5, lng: 43 },
  DK: { name: 'Denmark', lat: 56, lng: 10 },
  DM: { name: 'Dominica', lat: 15.4167, lng: -61.3333 },
  DO: { name: 'Dominican Republic', lat: 19, lng: -70.6667 },
  DZ: { name: 'Algeria', lat: 28, lng: 3 },
  EC: { name: 'Ecuador', lat: -2, lng: -77.5 },
  EE: { name: 'Estonia', lat: 59, lng: 26 },
  EG: { name: 'Egypt', lat: 27, lng: 30 },
  EH: { name: 'Western Sahara', lat: 24.5, lng: -13 },
  ER: { name: 'Eritrea', lat: 15, lng: 39 },
  ES: { name: 'Spain', lat: 40, lng: -4 },
  ET: { name: 'Ethiopia', lat: 8, lng: 38 },
  FI: { name: 'Finland', lat: 64, lng: 26 },
  FJ: { name: 'Fiji', lat: -18, lng: 175 },
  FK: { name: 'Falkland Islands', lat: -51.75, lng: -59 },
  FM: { name: 'Micronesia', lat: 6.9167, lng: 158.25 },
  FO: { name: 'Faroe Islands', lat: 62, lng: -7 },
  FR: { name: 'France', lat: 46, lng: 2 },
  GA: { name: 'Gabon', lat: -1, lng: 11.75 },
  GB: { name: 'United Kingdom', lat: 54, lng: -2 },
  GD: { name: 'Grenada', lat: 12.1167, lng: -61.6667 },
  GE: { name: 'Georgia', lat: 42, lng: 43.5 },
  GF: { name: 'French Guiana', lat: 4, lng: -53 },
  GG: { name: 'Guernsey', lat: 49.5, lng: -2.56 },
  GH: { name: 'Ghana', lat: 8, lng: -2 },
  GI: { name: 'Gibraltar', lat: 36.1833, lng: -5.3667 },
  GL: { name: 'Greenland', lat: 72, lng: -40 },
  GM: { name: 'Gambia', lat: 13.4667, lng: -16.5667 },
  GN: { name: 'Guinea', lat: 11, lng: -10 },
  GP: { name: 'Guadeloupe', lat: 16.25, lng: -61.5833 },
  GQ: { name: 'Equatorial Guinea', lat: 2, lng: 10 },
  GR: { name: 'Greece', lat: 39, lng: 22 },
  GS: { name: 'South Georgia', lat: -54.5, lng: -37 },
  GT: { name: 'Guatemala', lat: 15.5, lng: -90.25 },
  GU: { name: 'Guam', lat: 13.4667, lng: 144.7833 },
  GW: { name: 'Guinea-Bissau', lat: 12, lng: -15 },
  GY: { name: 'Guyana', lat: 5, lng: -59 },
  HK: { name: 'Hong Kong', lat: 22.25, lng: 114.1667 },
  HM: { name: 'Heard & McDonald Islands', lat: -53.1, lng: 72.5167 },
  HN: { name: 'Honduras', lat: 15, lng: -86.5 },
  HR: { name: 'Croatia', lat: 45.1667, lng: 15.5 },
  HT: { name: 'Haiti', lat: 19, lng: -72.4167 },
  HU: { name: 'Hungary', lat: 47, lng: 20 },
  ID: { name: 'Indonesia', lat: -5, lng: 120 },
  IE: { name: 'Ireland', lat: 53, lng: -8 },
  IL: { name: 'Israel', lat: 31.5, lng: 34.75 },
  IM: { name: 'Isle of Man', lat: 54.23, lng: -4.55 },
  IN: { name: 'India', lat: 20, lng: 77 },
  IO: { name: 'British Indian Ocean Territory', lat: -6, lng: 71.5 },
  IQ: { name: 'Iraq', lat: 33, lng: 44 },
  IR: { name: 'Iran', lat: 32, lng: 53 },
  IS: { name: 'Iceland', lat: 65, lng: -18 },
  IT: { name: 'Italy', lat: 42.8333, lng: 12.8333 },
  JE: { name: 'Jersey', lat: 49.21, lng: -2.13 },
  JM: { name: 'Jamaica', lat: 18.25, lng: -77.5 },
  JO: { name: 'Jordan', lat: 31, lng: 36 },
  JP: { name: 'Japan', lat: 36, lng: 138 },
  KE: { name: 'Kenya', lat: 1, lng: 38 },
  KG: { name: 'Kyrgyzstan', lat: 41, lng: 75 },
  KH: { name: 'Cambodia', lat: 13, lng: 105 },
  KI: { name: 'Kiribati', lat: 1.4167, lng: 173 },
  KM: { name: 'Comoros', lat: -12.1667, lng: 44.25 },
  KN: { name: 'Saint Kitts and Nevis', lat: 17.3333, lng: -62.75 },
  KP: { name: 'North Korea', lat: 40, lng: 127 },
  KR: { name: 'South Korea', lat: 37, lng: 127.5 },
  KW: { name: 'Kuwait', lat: 29.3375, lng: 47.6581 },
  KY: { name: 'Cayman Islands', lat: 19.5, lng: -80.5 },
  KZ: { name: 'Kazakhstan', lat: 48, lng: 68 },
  LA: { name: 'Laos', lat: 18, lng: 105 },
  LB: { name: 'Lebanon', lat: 33.8333, lng: 35.8333 },
  LC: { name: 'Saint Lucia', lat: 13.8833, lng: -61.1333 },
  LI: { name: 'Liechtenstein', lat: 47.1667, lng: 9.5333 },
  LK: { name: 'Sri Lanka', lat: 7, lng: 81 },
  LR: { name: 'Liberia', lat: 6.5, lng: -9.5 },
  LS: { name: 'Lesotho', lat: -29.5, lng: 28.5 },
  LT: { name: 'Lithuania', lat: 56, lng: 24 },
  LU: { name: 'Luxembourg', lat: 49.75, lng: 6.1667 },
  LV: { name: 'Latvia', lat: 57, lng: 25 },
  LY: { name: 'Libya', lat: 25, lng: 17 },
  MA: { name: 'Morocco', lat: 32, lng: -5 },
  MC: { name: 'Monaco', lat: 43.7333, lng: 7.4 },
  MD: { name: 'Moldova', lat: 47, lng: 29 },
  ME: { name: 'Montenegro', lat: 42.5, lng: 19.3 },
  MF: { name: 'Saint Martin', lat: 18.0833, lng: -63.0667 },
  MG: { name: 'Madagascar', lat: -20, lng: 47 },
  MH: { name: 'Marshall Islands', lat: 9, lng: 168 },
  MK: { name: 'North Macedonia', lat: 41.8333, lng: 22 },
  ML: { name: 'Mali', lat: 17, lng: -4 },
  MM: { name: 'Myanmar', lat: 22, lng: 98 },
  MN: { name: 'Mongolia', lat: 46, lng: 105 },
  MO: { name: 'Macao', lat: 22.1667, lng: 113.55 },
  MP: { name: 'Northern Mariana Islands', lat: 15.2, lng: 145.75 },
  MQ: { name: 'Martinique', lat: 14.6667, lng: -61 },
  MR: { name: 'Mauritania', lat: 20, lng: -12 },
  MS: { name: 'Montserrat', lat: 16.75, lng: -62.2 },
  MT: { name: 'Malta', lat: 35.8333, lng: 14.5833 },
  MU: { name: 'Mauritius', lat: -20.2833, lng: 57.55 },
  MV: { name: 'Maldives', lat: 3.25, lng: 73 },
  MW: { name: 'Malawi', lat: -13.5, lng: 34 },
  MX: { name: 'Mexico', lat: 23, lng: -102 },
  MY: { name: 'Malaysia', lat: 2.5, lng: 112.5 },
  MZ: { name: 'Mozambique', lat: -18.25, lng: 35 },
  NA: { name: 'Namibia', lat: -22, lng: 17 },
  NC: { name: 'New Caledonia', lat: -21.5, lng: 165.5 },
  NE: { name: 'Niger', lat: 16, lng: 8 },
  NF: { name: 'Norfolk Island', lat: -29.0333, lng: 167.95 },
  NG: { name: 'Nigeria', lat: 10, lng: 8 },
  NI: { name: 'Nicaragua', lat: 13, lng: -85 },
  NL: { name: 'Netherlands', lat: 52.5, lng: 5.75 },
  NO: { name: 'Norway', lat: 62, lng: 10 },
  NP: { name: 'Nepal', lat: 28, lng: 84 },
  NR: { name: 'Nauru', lat: -0.5333, lng: 166.9167 },
  NU: { name: 'Niue', lat: -19.0333, lng: -169.8667 },
  NZ: { name: 'New Zealand', lat: -41, lng: 174 },
  OM: { name: 'Oman', lat: 21, lng: 57 },
  PA: { name: 'Panama', lat: 9, lng: -80 },
  PE: { name: 'Peru', lat: -10, lng: -76 },
  PF: { name: 'French Polynesia', lat: -15, lng: -140 },
  PG: { name: 'Papua New Guinea', lat: -6, lng: 147 },
  PH: { name: 'Philippines', lat: 13, lng: 122 },
  PK: { name: 'Pakistan', lat: 30, lng: 70 },
  PL: { name: 'Poland', lat: 52, lng: 20 },
  PM: { name: 'Saint Pierre & Miquelon', lat: 46.8333, lng: -56.3333 },
  PN: { name: 'Pitcairn Islands', lat: -24.7, lng: -127.4 },
  PR: { name: 'Puerto Rico', lat: 18.25, lng: -66.5 },
  PS: { name: 'Palestine', lat: 32, lng: 35.25 },
  PT: { name: 'Portugal', lat: 39.5, lng: -8 },
  PW: { name: 'Palau', lat: 7.5, lng: 134.5 },
  PY: { name: 'Paraguay', lat: -23, lng: -58 },
  QA: { name: 'Qatar', lat: 25.5, lng: 51.25 },
  RE: { name: 'Réunion', lat: -21.1, lng: 55.6 },
  RO: { name: 'Romania', lat: 46, lng: 25 },
  RS: { name: 'Serbia', lat: 44, lng: 21 },
  RU: { name: 'Russia', lat: 60, lng: 100 },
  RW: { name: 'Rwanda', lat: -2, lng: 30 },
  SA: { name: 'Saudi Arabia', lat: 25, lng: 45 },
  SB: { name: 'Solomon Islands', lat: -8, lng: 159 },
  SC: { name: 'Seychelles', lat: -4.5833, lng: 55.6667 },
  SD: { name: 'Sudan', lat: 15, lng: 30 },
  SE: { name: 'Sweden', lat: 62, lng: 15 },
  SG: { name: 'Singapore', lat: 1.3667, lng: 103.8 },
  SH: { name: 'Saint Helena', lat: -15.9333, lng: -5.7 },
  SI: { name: 'Slovenia', lat: 46, lng: 15 },
  SJ: { name: 'Svalbard & Jan Mayen', lat: 78, lng: 20 },
  SK: { name: 'Slovakia', lat: 48.6667, lng: 19.5 },
  SL: { name: 'Sierra Leone', lat: 8.5, lng: -11.5 },
  SM: { name: 'San Marino', lat: 43.7667, lng: 12.4167 },
  SN: { name: 'Senegal', lat: 14, lng: -14 },
  SO: { name: 'Somalia', lat: 10, lng: 49 },
  SR: { name: 'Suriname', lat: 4, lng: -56 },
  SS: { name: 'South Sudan', lat: 8, lng: 30 },
  ST: { name: 'São Tomé & Príncipe', lat: 1, lng: 7 },
  SV: { name: 'El Salvador', lat: 13.8333, lng: -88.9167 },
  SX: { name: 'Sint Maarten', lat: 18.0333, lng: -63.05 },
  SY: { name: 'Syria', lat: 35, lng: 38 },
  SZ: { name: 'Eswatini', lat: -26.5, lng: 31.5 },
  TC: { name: 'Turks and Caicos Islands', lat: 21.75, lng: -71.5833 },
  TD: { name: 'Chad', lat: 15, lng: 19 },
  TF: { name: 'French Southern Territories', lat: -43, lng: 67 },
  TG: { name: 'Togo', lat: 8, lng: 1.1667 },
  TH: { name: 'Thailand', lat: 15, lng: 100 },
  TJ: { name: 'Tajikistan', lat: 39, lng: 71 },
  TK: { name: 'Tokelau', lat: -9, lng: -172 },
  TL: { name: 'Timor-Leste', lat: -8.55, lng: 125.5167 },
  TM: { name: 'Turkmenistan', lat: 40, lng: 60 },
  TN: { name: 'Tunisia', lat: 34, lng: 9 },
  TO: { name: 'Tonga', lat: -20, lng: -175 },
  TR: { name: 'Turkey', lat: 39, lng: 35 },
  TT: { name: 'Trinidad and Tobago', lat: 11, lng: -61 },
  TV: { name: 'Tuvalu', lat: -8, lng: 178 },
  TW: { name: 'Taiwan', lat: 23.5, lng: 121 },
  TZ: { name: 'Tanzania', lat: -6, lng: 35 },
  UA: { name: 'Ukraine', lat: 49, lng: 32 },
  UG: { name: 'Uganda', lat: 1, lng: 32 },
  UM: { name: 'U.S. Minor Outlying Islands', lat: 19.3, lng: 166.6 },
  US: { name: 'United States', lat: 38, lng: -97 },
  UY: { name: 'Uruguay', lat: -33, lng: -56 },
  UZ: { name: 'Uzbekistan', lat: 41, lng: 64 },
  VA: { name: 'Vatican City', lat: 41.9, lng: 12.45 },
  VC: { name: 'Saint Vincent & Grenadines', lat: 13.25, lng: -61.2 },
  VE: { name: 'Venezuela', lat: 8, lng: -66 },
  VG: { name: 'British Virgin Islands', lat: 18.5, lng: -64.5 },
  VI: { name: 'U.S. Virgin Islands', lat: 18.3333, lng: -64.8333 },
  VN: { name: 'Vietnam', lat: 16, lng: 106 },
  VU: { name: 'Vanuatu', lat: -16, lng: 167 },
  WF: { name: 'Wallis & Futuna', lat: -13.3, lng: -176.2 },
  WS: { name: 'Samoa', lat: -13.5833, lng: -172.3333 },
  XK: { name: 'Kosovo', lat: 42.6, lng: 20.9 },
  YE: { name: 'Yemen', lat: 15, lng: 48 },
  YT: { name: 'Mayotte', lat: -12.8333, lng: 45.1667 },
  ZA: { name: 'South Africa', lat: -29, lng: 24 },
  ZM: { name: 'Zambia', lat: -15, lng: 30 },
  ZW: { name: 'Zimbabwe', lat: -20, lng: 30 },
};

export const getCentroidByCode = (code: string): CountryCentroid | undefined => {
  if (!code) return undefined;
  return COUNTRY_CENTROIDS[code.toUpperCase()];
};
